const EARTH_RADIUS_M = 6371000;
export const ANDROID_TRACK_MAX_SPEED_MPS = 45;
export const ANDROID_RESUME_MAX_SEQUENCE_GAP = 120;
export const ANDROID_RESUME_MIN_SEQUENCE_COVERAGE = 0.08;

export function shouldForceFullAndroidResumeSnapshot(
  backgroundMs,
  thresholdMs = 10000
) {
  const elapsed = Number(backgroundMs);
  const threshold = Math.max(0, Number(thresholdMs) || 0);
  return Number.isFinite(elapsed) && elapsed >= threshold;
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const finitePositive = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export function getAndroidTrackSequenceCoverage(points, options = {}) {
  const maxSequenceGap = Number(
    options.maxSequenceGap ?? ANDROID_RESUME_MAX_SEQUENCE_GAP
  );
  const minCoverage = Number(
    options.minCoverage ?? ANDROID_RESUME_MIN_SEQUENCE_COVERAGE
  );
  const sequences = [...new Set(
    (Array.isArray(points) ? points : [])
      .map((point) => Number(point?.seq))
      .filter((seq) => Number.isFinite(seq) && seq >= 0)
  )].sort((a, b) => a - b);

  let maxGap = 0;
  for (let index = 1; index < sequences.length; index += 1) {
    maxGap = Math.max(maxGap, sequences[index] - sequences[index - 1]);
  }
  const first = sequences.length ? sequences[0] : null;
  const last = sequences.length ? sequences[sequences.length - 1] : null;
  const span = first != null && last != null ? last - first + 1 : 0;
  const coverage = span > 0 ? sequences.length / span : 1;
  const sparse =
    span >= maxSequenceGap &&
    (maxGap >= maxSequenceGap || coverage < minCoverage);

  return {
    first,
    last,
    count: sequences.length,
    span,
    maxGap,
    coverage,
    sparse,
  };
}

export function shouldImmediatelyResyncAndroidTrack(
  points,
  lastNativeSequence,
  options = {}
) {
  const list = Array.isArray(points) ? points : [];
  const minPoints = Number(options.minPoints ?? 25);
  const sequenceStats = getAndroidTrackSequenceCoverage(list, options);
  const nativeSequence = Number(lastNativeSequence);
  const missingSequenceMetadata =
    list.length > 0 &&
    Number.isFinite(nativeSequence) &&
    nativeSequence >= 0 &&
    sequenceStats.count === 0;

  return (
    list.length < minPoints ||
    sequenceStats.sparse ||
    missingSequenceMetadata
  );
}

export function androidTrackDistanceMeters(a, b) {
  if (!a || !b) return 0;
  const lat1 = Number(a.lat);
  const lon1 = Number(a.lon);
  const lat2 = Number(b.lat);
  const lon2 = Number(b.lon);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return 0;

  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const rLat1 = toRad(lat1);
  const rLat2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export function getAndroidReportedSpeed(point) {
  const speed = Number(point?.spd ?? point?.speed);
  return Number.isFinite(speed) && speed >= 0 ? speed : null;
}

export function getAndroidReportedSpeedAccuracy(point) {
  const accuracy = Number(
    point?.spdAcc ?? point?.speedAcc ?? point?.speedAccuracy
  );
  return Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null;
}

export function getAndroidStationaryRadius(prev, next, options = {}) {
  const minRadiusM = Number(options.minRadiusM ?? 10);
  const maxRadiusM = Number(options.maxRadiusM ?? 40);
  const accuracyFactor = Number(options.accuracyFactor ?? 1.25);
  const prevAcc = finitePositive(prev?.acc);
  const nextAcc = finitePositive(next?.acc);

  if (prevAcc == null && nextAcc == null) return minRadiusM;
  const combinedAccuracy = Math.hypot(prevAcc || nextAcc, nextAcc || prevAcc);
  return clamp(combinedAccuracy * accuracyFactor, minRadiusM, maxRadiusM);
}

export function shouldKeepAndroidTrackPoint(prev, next, options = {}) {
  if (!prev || !next) return true;

  const prevTs = Number(prev.ts ?? prev.time);
  const nextTs = Number(next.ts ?? next.time);
  const dtMs =
    Number.isFinite(prevTs) && Number.isFinite(nextTs) ? nextTs - prevTs : 0;
  if (dtMs <= 0) return false;

  const distanceM = androidTrackDistanceMeters(prev, next);
  const gapMs = Number(options.gapMs ?? 120000);
  const maxPlausibleSpeedMps = Number(
    options.maxPlausibleSpeedMps ?? ANDROID_TRACK_MAX_SPEED_MPS
  );
  const calculatedSpeedMps = dtMs > 0 ? distanceM / (dtMs / 1000) : 0;
  if (
    dtMs < gapMs &&
    Number.isFinite(maxPlausibleSpeedMps) &&
    calculatedSpeedMps > maxPlausibleSpeedMps
  ) {
    return false;
  }
  const speedMps = getAndroidReportedSpeed(next);
  const speedAccuracyMps = getAndroidReportedSpeedAccuracy(next);
  const stationarySpeedMps = Number(options.stationarySpeedMps ?? 0.5);
  const minMovingConfidenceMps = Number(
    options.minMovingConfidenceMps ?? 0.2
  );
  if (
    speedMps != null &&
    speedAccuracyMps != null &&
    speedMps - speedAccuracyMps < minMovingConfidenceMps
  ) {
    return false;
  }
  const stationaryLike =
    speedMps == null ||
    speedMps < stationarySpeedMps ||
    calculatedSpeedMps < stationarySpeedMps;

  if (stationaryLike) {
    return distanceM >= getAndroidStationaryRadius(prev, next, options);
  }

  if (dtMs >= gapMs) return true;

  const movingMinStepM = Number(options.movingMinStepM ?? 3);
  return distanceM >= movingMinStepM;
}

export function shouldMarkAndroidRecordingGap(prev, next, options = {}) {
  if (!prev || !next) return false;

  const prevTs = Number(prev.ts ?? prev.time);
  const nextTs = Number(next.ts ?? next.time);
  const dtMs =
    Number.isFinite(prevTs) && Number.isFinite(nextTs) ? nextTs - prevTs : 0;
  const minGapMs = Number(options.minGapMs ?? 30000);
  if (dtMs < minGapMs) return false;

  const distanceM = androidTrackDistanceMeters(prev, next);
  const minDistanceM = Number(options.minDistanceM ?? 200);
  if (distanceM < minDistanceM) return false;

  // Once both the sampling gap and the displacement are large, there is no
  // recorded geometry between the fixes. Joining them would invent a route
  // (usually the long straight line seen after Android resumes/backgrounds),
  // even when distance / elapsed time happens to look like walking speed.
  if (options.requireImplausibleSpeed !== true) return true;

  const speedMps = distanceM / (dtMs / 1000);
  const maxPlausibleSpeedMps = Number(options.maxPlausibleSpeedMps ?? 15);
  return speedMps > maxPlausibleSpeedMps;
}

export function removeAndroidTrackExcursions(points, options = {}) {
  if (!Array.isArray(points) || points.length < 3) {
    return Array.isArray(points) ? [...points] : [];
  }

  const excursionDistanceM = Number(options.excursionDistanceM ?? 50);
  const recoveryRadiusM = Number(options.excursionRecoveryRadiusM ?? 40);
  const suspiciousAccuracyM = Number(
    options.excursionSuspiciousAccuracyM ?? 25
  );
  const maxLookahead = Math.max(
    2,
    Number(options.excursionMaxLookahead ?? 120)
  );
  const maxExcursionDurationMs = Number(
    options.excursionMaxDurationMs ?? 5 * 60 * 1000
  );
  const impossibleEntrySpeedMps = Number(
    options.excursionImpossibleEntrySpeedMps ?? ANDROID_TRACK_MAX_SPEED_MPS
  );
  const impossibleEntryRecoveryRadiusM = Number(
    options.excursionImpossibleEntryRecoveryRadiusM ?? 100
  );
  const filtered = [];

  for (let index = 0; index < points.length; index += 1) {
    const anchor = filtered[filtered.length - 1];
    const current = points[index];
    if (!anchor) {
      filtered.push(current);
      continue;
    }

    const currentDistanceM = androidTrackDistanceMeters(anchor, current);
    if (currentDistanceM < excursionDistanceM) {
      filtered.push(current);
      continue;
    }

    const lastRecoveryIndex = Math.min(
      points.length - 1,
      index + maxLookahead
    );
    const anchorTs = Number(anchor?.ts ?? anchor?.time) || 0;
    const currentTs = Number(current?.ts ?? current?.time) || 0;
    const entryDurationMs = Math.max(0, currentTs - anchorTs);
    const entrySpeedMps = entryDurationMs > 0
      ? currentDistanceM / (entryDurationMs / 1000)
      : 0;
    const impossibleEntry =
      entryDurationMs > 0 &&
      entryDurationMs < maxExcursionDurationMs &&
      entrySpeedMps > impossibleEntrySpeedMps;
    let recoveryIndex = -1;
    for (let candidateIndex = index + 1; candidateIndex <= lastRecoveryIndex; candidateIndex += 1) {
      const candidateTs = Number(
        points[candidateIndex]?.ts ?? points[candidateIndex]?.time
      ) || 0;
      const candidateDurationMs =
        anchorTs && candidateTs ? candidateTs - anchorTs : 0;
      const recoveryDistanceM = androidTrackDistanceMeters(
        anchor,
        points[candidateIndex]
      );
      const normalRecovery = recoveryDistanceM <= recoveryRadiusM;
      const impossibleEntryRecovery =
        impossibleEntry &&
        candidateDurationMs > 0 &&
        candidateDurationMs <= maxExcursionDurationMs &&
        recoveryDistanceM <= impossibleEntryRecoveryRadiusM;
      if (normalRecovery || impossibleEntryRecovery) {
        recoveryIndex = candidateIndex;
        break;
      }
    }

    const excursionHasPoorFix =
      recoveryIndex >= 0 &&
      points.slice(index, recoveryIndex).some((point) => {
        if (point?.acc == null) return true;
        const accuracy = Number(point?.acc);
        return !Number.isFinite(accuracy) || accuracy >= suspiciousAccuracyM;
      });

    if (recoveryIndex >= 0 && (excursionHasPoorFix || impossibleEntry)) {
      index = recoveryIndex - 1;
      continue;
    }
    filtered.push(current);
  }

  return filtered;
}

export function filterAndroidTrackPoints(points, options = {}) {
  if (!Array.isArray(points) || points.length < 2) {
    return Array.isArray(points) ? [...points] : [];
  }

  const chronological = [...points]
    .filter(
      (point) =>
        Number.isFinite(Number(point?.lat)) &&
        Number.isFinite(Number(point?.lon)) &&
        Number.isFinite(Number(point?.ts ?? point?.time)) &&
        (() => {
          const accuracy = Number(point?.acc);
          const maxAccuracyM = Number(options.maxAccuracyM ?? 60);
          return (
            !Number.isFinite(accuracy) ||
            !Number.isFinite(maxAccuracyM) ||
            accuracy <= maxAccuracyM
          );
        })()
    )
    .sort(
      (a, b) =>
        Number(a.ts ?? a.time) - Number(b.ts ?? b.time)
    );
  if (chronological.length < 2) return chronological;

  const withoutExcursions = removeAndroidTrackExcursions(
    chronological,
    options
  );
  if (withoutExcursions.length < 2) return withoutExcursions;

  const accepted = [withoutExcursions[0]];
  for (let index = 1; index < withoutExcursions.length; index += 1) {
    const point = withoutExcursions[index];
    const anchor = accepted[accepted.length - 1];
    if (shouldKeepAndroidTrackPoint(anchor, point, options)) {
      accepted.push(point);
    }
  }
  return removeAndroidTrackExcursions(accepted, options);
}
